import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowRight, ShieldCheck, Coins, Sparkles, User } from 'lucide-react';
import { sellerAPI } from '../utils/api';

const SellerLogin = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await sellerAPI.login(formData.identifier, formData.password);
      if (response.data.success) {
        const { token } = response.data;
        sessionStorage.setItem('sellerToken', token);
        navigate('/seller/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials or contact Admin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060a17] flex items-center justify-center p-4 font-['Outfit'] overflow-hidden relative">
      {/* Ambient background spheres */}
      <div className="absolute top-[-10%] left-[-10%] w-[45%] h-[45%] bg-amber-500/15 blur-[140px] rounded-full animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[45%] h-[45%] bg-yellow-500/15 blur-[140px] rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-[40%] right-[20%] w-[30%] h-[30%] bg-pink-600/10 blur-[140px] rounded-full"></div>

      <div className="w-full max-w-[460px] relative z-10">
        {/* Glassmorphic Container */}
        <div className="bg-slate-900/60 backdrop-blur-2xl border border-white/10 rounded-[36px] p-8 md:p-12 shadow-2xl overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600"></div>

          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-tr from-amber-500 via-yellow-400 to-amber-600 p-[2px] mb-5 group transition-transform hover:scale-105 duration-300 shadow-xl shadow-amber-500/20">
               <div className="w-full h-full bg-[#0a0f24] rounded-[22px] flex items-center justify-center">
                  <Coins size={38} className="text-amber-400 group-hover:rotate-12 transition-transform duration-300" />
               </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-2">
              COIN <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">SELLER</span>
            </h1>
            <p className="text-slate-400 text-sm font-medium">Merchant Wallet & Direct Coin Transfer Portal</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/25 rounded-2xl text-red-400 text-sm font-bold text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-extrabold text-slate-300 uppercase tracking-wider mb-2">
                Seller ID / Email / Phone / XOXO ID
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <User size={18} />
                </div>
                <input
                  type="text"
                  name="identifier"
                  required
                  value={formData.identifier}
                  onChange={handleInputChange}
                  placeholder="e.g. 10001 or seller@xoxo.com"
                  className="w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-medium"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-extrabold text-slate-300 uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-medium"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-base shadow-xl shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-[1.02] active:scale-[0.98] transition duration-200 flex items-center justify-center gap-2 mt-3 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Sign In As Coin Seller</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Quick links & badge */}
          <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between text-xs text-slate-400">
            <span className="flex items-center gap-1 text-amber-400 font-bold">
              <ShieldCheck size={14} /> Official Merchant Security
            </span>
            <a href="/login" className="hover:text-white transition font-medium">
              Admin Login →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerLogin;
